import React, { useState, useMemo } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/SeasonCalendar.css';
import { Card } from './ui/card';

const localizer = momentLocalizer(moment);

const SeasonCalendar = ({ seasons = [], stateCode }) => {
  const [view, setView] = useState(Views.MONTH);
  // Start calendar in October 2024 when most hunting seasons begin
  const [date, setDate] = useState(new Date(2024, 9, 1)); // October 2024
  const [activeFilter, setActiveFilter] = useState(null); // Track which season type is selected

  // Define season types organized by species first, then method
  const seasonTypes = [
    // Deer categories
    { 
      id: 'deer-archery',
      label: 'Deer → Archery', 
      color: 'bg-emerald-600',
      species: 'Deer',
      method: 'Archery',
      filter: (season) => {
        const species = (season.species_group || '').toLowerCase();
        const method = (season.method || '').toLowerCase();
        return species.includes('deer') && (method.includes('archery') || method.includes('bow'));
      }
    },
    { 
      id: 'deer-muzzleloader',
      label: 'Deer → Muzzleloader', 
      color: 'bg-amber-600',
      species: 'Deer',
      method: 'Muzzleloader',
      filter: (season) => {
        const species = (season.species_group || '').toLowerCase();
        const method = (season.method || '').toLowerCase();
        return species.includes('deer') && method.includes('muzzle');
      }
    },
    { 
      id: 'deer-firearms',
      label: 'Deer → Firearms', 
      color: 'bg-red-600',
      species: 'Deer',
      method: 'Firearms',
      filter: (season) => {
        const species = (season.species_group || '').toLowerCase();
        const method = (season.method || '').toLowerCase();
        return species.includes('deer') && (method.includes('firearm') || method.includes('rifle') || method.includes('gun'));
      }
    },
    { 
      id: 'deer-general',
      label: 'Deer → General/Other', 
      color: 'bg-green-700',
      species: 'Deer',
      method: 'General',
      filter: (season) => {
        const species = (season.species_group || '').toLowerCase();
        const method = (season.method || '').toLowerCase();
        return species.includes('deer') && !method.includes('archery') && !method.includes('bow') && 
               !method.includes('muzzle') && !method.includes('firearm') && !method.includes('rifle');
      }
    },
    // Turkey categories
    { 
      id: 'turkey-spring',
      label: 'Turkey → Spring', 
      color: 'bg-purple-600',
      species: 'Turkey',
      method: 'Spring',
      filter: (season) => {
        const species = (season.species_group || '').toLowerCase();
        const title = (season.title || '').toLowerCase();
        return species.includes('turkey') && title.includes('spring');
      }
    },
    { 
      id: 'turkey-fall',
      label: 'Turkey → Fall', 
      color: 'bg-violet-700',
      species: 'Turkey',
      method: 'Fall',
      filter: (season) => {
        const species = (season.species_group || '').toLowerCase();
        const title = (season.title || '').toLowerCase();
        return species.includes('turkey') && (title.includes('fall') || !title.includes('spring'));
      }
    },
    // Bear category
    { 
      id: 'bear',
      label: 'Bear → All Methods', 
      color: 'bg-yellow-900',
      species: 'Bear',
      method: 'All',
      filter: (season) => {
        const species = (season.species_group || '').toLowerCase();
        return species.includes('bear');
      }
    },
    // Bird categories
    { 
      id: 'waterfowl',
      label: 'Waterfowl → Ducks/Geese', 
      color: 'bg-sky-600',
      species: 'Waterfowl',
      method: 'All',
      filter: (season) => {
        const species = (season.species_group || '').toLowerCase();
        return species.includes('waterfowl') || species.includes('duck') || species.includes('goose') || 
               species.includes('teal') || species.includes('merganser');
      }
    },
    { 
      id: 'dove',
      label: 'Dove → All Seasons', 
      color: 'bg-pink-600',
      species: 'Dove',
      method: 'All',
      filter: (season) => {
        const species = (season.species_group || '').toLowerCase();
        return species.includes('dove') || species.includes('pigeon');
      }
    },
    // Small Game category
    { 
      id: 'small-game',
      label: 'Small Game → All Species', 
      color: 'bg-slate-600',
      species: 'Small Game',
      method: 'All',
      filter: (season) => {
        const species = (season.species_group || '').toLowerCase();
        return species.includes('small game') || species.includes('rabbit') || species.includes('squirrel') || 
               species.includes('raccoon') || species.includes('opossum') || species.includes('furbearer');
      }
    },
    // Other categories
    { 
      id: 'elk',
      label: 'Elk → All Methods', 
      color: 'bg-orange-700',
      species: 'Elk',
      method: 'All',
      filter: (season) => {
        const species = (season.species_group || '').toLowerCase();
        return species.includes('elk');
      }
    },
    { 
      id: 'other',
      label: 'Other → Various', 
      color: 'bg-gray-600',
      species: 'Other',
      method: 'All',
      filter: (season) => {
        const species = (season.species_group || '').toLowerCase();
        // Catch-all for species not covered above
        return !species.includes('deer') && !species.includes('turkey') && !species.includes('bear') && 
               !species.includes('waterfowl') && !species.includes('duck') && !species.includes('goose') && 
               !species.includes('dove') && !species.includes('small game') && !species.includes('rabbit') && 
               !species.includes('squirrel') && !species.includes('elk');
      }
    }
  ];

  // Define color mapping for different species/methods
  const getEventColor = (season) => {
    const species = (season.species_group || '').toLowerCase();
    const method = (season.method || '').toLowerCase();
    
    // Species-based colors
    if (species.includes('deer')) {
      if (method.includes('archery')) return { bg: '#10b981', text: 'white' }; // emerald
      if (method.includes('muzzle')) return { bg: '#f59e0b', text: 'white' }; // amber
      if (method.includes('firearm')) return { bg: '#ef4444', text: 'white' }; // red
      return { bg: '#059669', text: 'white' }; // green
    }
    if (species.includes('turkey')) {
      if (season.title?.toLowerCase().includes('spring')) return { bg: '#8b5cf6', text: 'white' }; // violet
      if (season.title?.toLowerCase().includes('fall')) return { bg: '#f97316', text: 'white' }; // orange
      return { bg: '#7c3aed', text: 'white' }; // purple
    }
    if (species.includes('bear')) return { bg: '#92400e', text: 'white' }; // brown
    if (species.includes('waterfowl') || species.includes('duck') || species.includes('goose')) {
      return { bg: '#0ea5e9', text: 'white' }; // sky blue
    }
    if (species.includes('small game') || species.includes('rabbit') || species.includes('squirrel')) {
      return { bg: '#64748b', text: 'white' }; // slate
    }
    if (species.includes('dove')) return { bg: '#ec4899', text: 'white' }; // pink
    
    // Default color
    return { bg: '#475569', text: 'white' }; // gray
  };

  // Parse season dates - make year-agnostic by using current calendar view year
  const parseSeasonDates = (season) => {
    const dates = [];
    const calendarYear = date.getFullYear(); // Use the calendar's current year
    
    // First, check if dates are in ISO format (YYYY-MM-DD)
    if (season.start_date && season.end_date) {
      // Check if it's ISO format
      if (season.start_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Parse ISO date strings
        const startDate = new Date(season.start_date);
        const endDate = new Date(season.end_date);
        
        if (!isNaN(startDate) && !isNaN(endDate)) {
          // Extract month and day, apply to calendar year
          const startMonth = startDate.getMonth(); // 0-based
          const startDay = startDate.getDate();
          const endMonth = endDate.getMonth();
          const endDay = endDate.getDate();
          
          // Create dates using calendar's current year
          const start = new Date(calendarYear, startMonth, startDay);
          let end = new Date(calendarYear, endMonth, endDay, 23, 59, 59); // Include full day
          
          // Handle year rollover for seasons that span across years (e.g., Dec to Jan)
          if (end < start) {
            end = new Date(calendarYear + 1, endMonth, endDay, 23, 59, 59);
          }
          
          dates.push({ start, end });
          return dates;
        }
      } else {
        // Handle text format dates like "September 29" or "October 1"
        const startStr = season.start_date;
        const endStr = season.end_date;
        
        // Parse month and day from text format
        let start = moment(`${startStr} ${calendarYear}`, 'MMMM D YYYY');
        if (!start.isValid()) {
          start = moment(`${startStr} ${calendarYear}`, 'MMM D YYYY');
        }
        
        let end = moment(`${endStr} ${calendarYear}`, 'MMMM D YYYY');
        if (!end.isValid()) {
          end = moment(`${endStr} ${calendarYear}`, 'MMM D YYYY');
        }
        
        if (start.isValid() && end.isValid()) {
          // Handle year rollover
          if (end.isBefore(start)) {
            end.add(1, 'year');
          }
          end.endOf('day');
          dates.push({ start: start.toDate(), end: end.toDate() });
          return dates;
        }
      }
    }
    
    // Fallback: Parse from title field that may contain date ranges like "September 7 through March 31"
    const titleText = season.title || '';
    
    // Handle "Month Day through Month Day" format
    const throughMatch = titleText.match(/([A-Za-z]+)\s+(\d{1,2})[,\s]+(?:\d{4}\s+)?through\s+([A-Za-z]+)\s+(\d{1,2})/i);
    if (throughMatch) {
      const startMonth = throughMatch[1];
      const startDay = parseInt(throughMatch[2]);
      const endMonth = throughMatch[3];
      const endDay = parseInt(throughMatch[4]);
      
      // Parse dates with moment
      let start = moment(`${startMonth} ${startDay} ${calendarYear}`, 'MMMM D YYYY');
      if (!start.isValid()) {
        start = moment(`${startMonth} ${startDay} ${calendarYear}`, 'MMM D YYYY');
      }
      
      let end = moment(`${endMonth} ${endDay} ${calendarYear}`, 'MMMM D YYYY');
      if (!end.isValid()) {
        end = moment(`${endMonth} ${endDay} ${calendarYear}`, 'MMM D YYYY');
      }
      
      if (start.isValid() && end.isValid()) {
        // Handle year rollover
        if (end.isBefore(start)) {
          end.add(1, 'year');
        }
        end.endOf('day');
        dates.push({ start: start.toDate(), end: end.toDate() });
      }
    }
    
    // Handle multiple date ranges with "and" separator
    const andMatches = titleText.match(/([A-Za-z]+)\s+(\d{1,2})\s+through\s+([A-Za-z]+)\s+(\d{1,2})/gi) || [];
    andMatches.forEach(match => {
      const parts = match.match(/([A-Za-z]+)\s+(\d{1,2})\s+through\s+([A-Za-z]+)\s+(\d{1,2})/i);
      if (parts && dates.length === 0) { // Only if we haven't already parsed dates
        const startMonth = parts[1];
        const startDay = parseInt(parts[2]);
        const endMonth = parts[3];
        const endDay = parseInt(parts[4]);
        
        let start = moment(`${startMonth} ${startDay} ${calendarYear}`, 'MMMM D YYYY');
        if (!start.isValid()) {
          start = moment(`${startMonth} ${startDay} ${calendarYear}`, 'MMM D YYYY');
        }
        
        let end = moment(`${endMonth} ${endDay} ${calendarYear}`, 'MMMM D YYYY');
        if (!end.isValid()) {
          end = moment(`${endMonth} ${endDay} ${calendarYear}`, 'MMM D YYYY');
        }
        
        if (start.isValid() && end.isValid()) {
          if (end.isBefore(start)) {
            end.add(1, 'year');
          }
          end.endOf('day');
          dates.push({ start: start.toDate(), end: end.toDate() });
        }
      }
    });
    
    return dates;
  };

  // Transform seasons into calendar events with optional filtering
  const events = useMemo(() => {
    const allEvents = [];
    
    console.log(`Processing ${seasons.length} seasons for calendar`);
    
    // Filter seasons based on active filter
    const filteredSeasons = activeFilter 
      ? seasons.filter(season => {
          const activeType = seasonTypes.find(type => type.id === activeFilter);
          return activeType ? activeType.filter(season) : true;
        })
      : seasons;
    
    console.log(`After filtering: ${filteredSeasons.length} seasons`);
    
    filteredSeasons.forEach((season, index) => {
      const dateRanges = parseSeasonDates(season);
      const colors = getEventColor(season);
      
      if (index < 3) {
        console.log(`Season ${index}:`, season.title);
        console.log(`  Parsed date ranges:`, dateRanges);
      }
      
      dateRanges.forEach((range, rangeIndex) => {
        // Create hierarchical title: Species → Method [Area]
        const species = season.species_group || 'Unknown';
        const method = season.method || '';
        const areas = season.areas && season.areas.length > 0 
          ? season.areas.length > 2 
            ? `[${season.areas[0]}, ${season.areas[1]}...]`
            : `[${season.areas.join(', ')}]`
          : '';
        
        // Format title with hierarchy indicator
        let eventTitle = species;
        if (method) {
          eventTitle += ` → ${method}`;
        }
        if (areas) {
          eventTitle += ` ${areas}`;
        }
        
        // Add bag limit if available
        if (season.bag_limit && !season.bag_limit.toLowerCase().includes('varies')) {
          eventTitle += ` (${season.bag_limit})`;
        }
        
        allEvents.push({
          id: `${season.id || index}-${rangeIndex}`,
          title: eventTitle,
          start: range.start,
          end: range.end,
          allDay: true,
          resource: season, // Store full season data for tooltip/popup
          color: colors
        });
      });
    });
    
    console.log(`Generated ${allEvents.length} calendar events`);
    if (allEvents.length > 0 && allEvents.length < 5) {
      console.log('Sample events:', allEvents);
    }
    
    return allEvents;
  }, [seasons, activeFilter]);

  // Custom event style
  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.color.bg,
        color: event.color.text,
        border: 'none',
        borderRadius: '3px',
        fontSize: '11px',
        padding: '2px 4px',
        cursor: 'pointer'
      }
    };
  };

  // Custom toolbar for calendar controls
  const CustomToolbar = (toolbar) => {
    const goToToday = () => toolbar.onNavigate('TODAY');
    const goToNext = () => toolbar.onNavigate('NEXT');
    const goToPrev = () => toolbar.onNavigate('PREV');
    const goToMonth = () => toolbar.onView(Views.MONTH);
    const goToWeek = () => toolbar.onView(Views.WEEK);
    const goToAgenda = () => toolbar.onView(Views.AGENDA);

    return (
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4 p-3 bg-black/20 rounded-lg">
        <div className="flex items-center gap-2">
          <button
            onClick={goToToday}
            className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-medium transition-colors"
          >
            Today
          </button>
          <button
            onClick={goToPrev}
            className="px-3 py-1 bg-white/10 hover:bg-white/20 text-white rounded text-sm transition-colors"
          >
            ←
          </button>
          <button
            onClick={goToNext}
            className="px-3 py-1 bg-white/10 hover:bg-white/20 text-white rounded text-sm transition-colors"
          >
            →
          </button>
        </div>
        
        <h2 className="text-lg font-semibold text-white">{toolbar.label}</h2>
        
        <div className="flex gap-2">
          <button
            onClick={goToMonth}
            className={`px-3 py-1 rounded text-sm transition-colors ${
              toolbar.view === Views.MONTH 
                ? 'bg-emerald-600 text-white' 
                : 'bg-white/10 hover:bg-white/20 text-white'
            }`}
          >
            Month
          </button>
          <button
            onClick={goToWeek}
            className={`px-3 py-1 rounded text-sm transition-colors ${
              toolbar.view === Views.WEEK 
                ? 'bg-emerald-600 text-white' 
                : 'bg-white/10 hover:bg-white/20 text-white'
            }`}
          >
            Week
          </button>
          <button
            onClick={goToAgenda}
            className={`px-3 py-1 rounded text-sm transition-colors ${
              toolbar.view === Views.AGENDA 
                ? 'bg-emerald-600 text-white' 
                : 'bg-white/10 hover:bg-white/20 text-white'
            }`}
          >
            List
          </button>
        </div>
      </div>
    );
  };

  // Custom event component for hierarchical display
  const CustomEvent = ({ event }) => {
    const season = event.resource;
    const parts = event.title.split(' → ');
    const hasMethod = parts.length > 1;
    
    return (
      <div className="text-xs p-0.5" title={`${season.title}\n${season.dates_text || ''}\nBag Limit: ${season.bag_limit || 'N/A'}`}>
        <div className="font-semibold truncate">
          {parts[0]} {/* Species */}
        </div>
        {hasMethod && (
          <div className="truncate opacity-90 text-[10px]">
            → {parts[1]} {/* Method and areas */}
          </div>
        )}
      </div>
    );
  };

  // Handle event click to show details
  const handleSelectEvent = (event) => {
    const season = event.resource;
    // You could open a modal here or expand details
    console.log('Season clicked:', season);
  };

  // Handle filter button click
  const handleFilterClick = (typeId) => {
    // Toggle filter - if clicking the same filter, clear it
    setActiveFilter(activeFilter === typeId ? null : typeId);
  };

  if (!seasons || seasons.length === 0) {
    return (
      <Card className="p-4 bg-black/40 backdrop-blur border-white/10">
        <div className="text-white text-center">No seasons data available for calendar view.</div>
      </Card>
    );
  }

  return (
    <Card className="p-4 bg-black/40 backdrop-blur border-white/10">
      <div className="mb-4">
        <h3 className="text-xl font-semibold text-white mb-2">
          {stateCode} Hunting Seasons Calendar
        </h3>
        <div className="text-sm text-white/70">
          Click on any season type below to filter the calendar. Click again to show all seasons.
        </div>
      </div>

      {/* Interactive Legend - Organized by Species → Method */}
      <div className="mb-4 p-3 bg-black/20 rounded-lg">
        <div className="text-sm font-semibold text-white mb-3">
          Filter by Category (Species → Method):
          {activeFilter && (
            <button 
              onClick={() => setActiveFilter(null)}
              className="ml-3 px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs text-white transition-colors"
            >
              ✕ Clear Filter
            </button>
          )}
        </div>
        
        {/* Group categories by species */}
        <div className="space-y-3">
          {/* Deer Categories */}
          <div>
            <div className="text-xs text-white/70 mb-1">DEER</div>
            <div className="flex flex-wrap gap-1.5">
              {seasonTypes.filter(t => t.species === 'Deer').map(type => (
                <button
                  key={type.id}
                  onClick={() => handleFilterClick(type.id)}
                  className={`inline-flex items-center px-2 py-1 rounded text-xs text-white transition-all cursor-pointer
                    ${type.color} hover:opacity-80 ${
                      activeFilter === type.id 
                        ? 'ring-2 ring-white ring-offset-2 ring-offset-black/20 scale-105' 
                        : activeFilter && activeFilter !== type.id
                        ? 'opacity-40'
                        : ''
                    }`}
                >
                  {type.method}
                  {activeFilter === type.id && ' ✓'}
                </button>
              ))}
            </div>
          </div>
          
          {/* Turkey Categories */}
          <div>
            <div className="text-xs text-white/70 mb-1">TURKEY</div>
            <div className="flex flex-wrap gap-1.5">
              {seasonTypes.filter(t => t.species === 'Turkey').map(type => (
                <button
                  key={type.id}
                  onClick={() => handleFilterClick(type.id)}
                  className={`inline-flex items-center px-2 py-1 rounded text-xs text-white transition-all cursor-pointer
                    ${type.color} hover:opacity-80 ${
                      activeFilter === type.id 
                        ? 'ring-2 ring-white ring-offset-2 ring-offset-black/20 scale-105' 
                        : activeFilter && activeFilter !== type.id
                        ? 'opacity-40'
                        : ''
                    }`}
                >
                  {type.method}
                  {activeFilter === type.id && ' ✓'}
                </button>
              ))}
            </div>
          </div>
          
          {/* Other Species */}
          <div>
            <div className="text-xs text-white/70 mb-1">OTHER SPECIES</div>
            <div className="flex flex-wrap gap-1.5">
              {seasonTypes.filter(t => !['Deer', 'Turkey'].includes(t.species)).map(type => (
                <button
                  key={type.id}
                  onClick={() => handleFilterClick(type.id)}
                  className={`inline-flex items-center px-2 py-1 rounded text-xs text-white transition-all cursor-pointer
                    ${type.color} hover:opacity-80 ${
                      activeFilter === type.id 
                        ? 'ring-2 ring-white ring-offset-2 ring-offset-black/20 scale-105' 
                        : activeFilter && activeFilter !== type.id
                        ? 'opacity-40'
                        : ''
                    }`}
                >
                  {type.species}
                  {activeFilter === type.id && ' ✓'}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {activeFilter && (
          <div className="mt-3 text-xs text-emerald-400 font-medium">
            Showing: {seasonTypes.find(t => t.id === activeFilter)?.label}
          </div>
        )}
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-lg p-2" style={{ minHeight: '600px' }}>
        {events.length > 0 ? (
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            view={view}
            onView={setView}
            date={date}
            onNavigate={setDate}
            style={{ height: 600 }}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={handleSelectEvent}
            components={{
              toolbar: CustomToolbar,
              event: CustomEvent
            }}
            views={[Views.MONTH, Views.WEEK, Views.AGENDA]}
            popup
            showMultiDayTimes
          />
        ) : (
          <div className="flex items-center justify-center h-96 text-gray-500">
            {activeFilter 
              ? `No seasons found for ${seasonTypes.find(t => t.id === activeFilter)?.label}`
              : 'No valid date ranges found in season data.'}
          </div>
        )}
      </div>
    </Card>
  );
};

export default SeasonCalendar;